import { Flex, xcss } from '@atlaskit/primitives';
import { getDatabaseContainerType, useAnalytics } from '@databases/analytics';
import usePanelState from '@databases/panel/usePanelState';
import { getAllPanelsForPanelRenderer } from '@databases/panel-types';
import React, { type FunctionComponent, useCallback, useMemo, useRef } from 'react';
import { type ImperativePanelHandle, Panel } from 'react-resizable-panels';
import styled from 'styled-components';

import PanelRenderer from './PanelRenderer';
import AccessibleResizeHandle from './accessible-resize-handle';
import PanelSizes from './panel/sizes';
import { createPanelStorageKey, getDefaultPanelSize } from './panel/utils';
import Z_INDEX from './z-index';

const SidebarPanel = styled(Panel)`
  z-index: ${Z_INDEX.SIDE_PANEL_CONTAINER};
  min-width: 300px; /* Ensure minimum width in pixels regardless of viewport size */
`;

const componentStyles = xcss({
  width: '100%',
  height: '100%',
  overflow: 'auto',
});

interface SidePanelProps {
  sidebar?: React.ReactNode | null;
  resizeDisabled?: boolean;
  contentId: string;
}

const SidePanel: FunctionComponent<SidePanelProps> = ({
  sidebar,
  resizeDisabled,
  contentId,
}) => {
  const { getPanelKey } = usePanelState();
  const panelKey = getPanelKey();
  const storageKey = useMemo(() => createPanelStorageKey(panelKey), [panelKey]);
  const analyticsClient = useAnalytics();

  const panelRef = useRef<ImperativePanelHandle>(null);

  const handleDragging = useCallback(
    (isDragging: boolean) => {
      if (!isDragging && panelKey && panelRef.current) {
        const panelSize = panelRef.current.getSize();
        analyticsClient.sendPanelResizedEvent({
          contentId,
          databaseContainer: getDatabaseContainerType(),
          panelKey,
          panelSize,
        });
      }
    },
    [analyticsClient, contentId, panelKey]
  );

  /**
   * If a sidebar component is passed, that takes priority.
   * If a panel key is set, this defers to the panel component.
   */
  const sidebarComponent = useMemo(() => {
    if (sidebar) {
      return sidebar;
    }

    if (panelKey) {
      const panels = getAllPanelsForPanelRenderer();

      return <PanelRenderer panels={panels} panel={panelKey} />;
    }

    return null;
  }, [panelKey, sidebar]);

  const sidebarPanelProps = useMemo(() => {
    const isDevtools = panelKey === 'DevtoolsPanel';
    const panelType = isDevtools ? 'devtools' : 'sidebar';
    const panelConfig = PanelSizes[panelType];

    return {
      order: panelConfig.order,
      maxSize: panelConfig.max,
      minSize: panelConfig.min,
      defaultSize: getDefaultPanelSize(panelType, storageKey),
      'data-testid': 'layout-right-side-panel',
    };
  }, [storageKey, panelKey]);

  const resizeHandleStyle = useMemo(
    () => ({
      // eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
      margin: 0,
    }),
    []
  );

  if (!sidebarComponent) {
    return null;
  }

  return (
    <>
      <AccessibleResizeHandle
        id="app-resize-handle"
        style={resizeHandleStyle}
        disabled={resizeDisabled}
        onDragging={handleDragging}
        panelRef={panelRef}
        minSize={sidebarPanelProps.minSize}
        maxSize={sidebarPanelProps.maxSize}
      />

      <SidebarPanel {...sidebarPanelProps} id="right-side-panel" ref={panelRef}>
        <Flex
          justifyContent="start"
          alignItems="center"
          direction="column"
          xcss={componentStyles}
        >
          {sidebarComponent}
        </Flex>
      </SidebarPanel>
    </>
  );
};

export default SidePanel;
